import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Search, Eye, X, Package, Check, Truck, FileText, Ban, PackageCheck } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import sessionService from '../../services/sessionService';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { formatMoney } from '../../utils/money';
import '../Sessions/SessionsPage.css';
import './DemandesPage.css';

const DemandesPage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { user, isAdmin, isLogistiqueCoordinator, isManager } = useAuth();
  const { canEdit } = usePermissions();
  const canEditDemandes = canEdit('demandes');

  const getCurrency = (row) => row?.supplier_devise || row?.centre_devise || user?.centres_details?.[0]?.devise || 'EUR';
  const toMoney = (value, row) => formatMoney(value, { locale: i18n.language, currency: getCurrency(row) });

  const statusActionMeta = (st) => {
    switch (st) {
      case 'PENDING_MANAGER':
        return { Icon: Check, title: t('demandes.actions.PENDING_MANAGER') };
      case 'NEW':
        return { Icon: Check, title: t('demandes.actions.NEW') };
      case 'ACCEPTED':
        return { Icon: Check, title: t('demandes.actions.ACCEPTED') };
      case 'SHIPPED':
        return { Icon: Truck, title: t('demandes.actions.SHIPPED') };
      case 'RECEIVED':
        return { Icon: PackageCheck, title: t('demandes.actions.RECEIVED') };
      case 'INVOICED':
        return { Icon: FileText, title: t('demandes.actions.INVOICED') };
      case 'CANCELLED':
        return { Icon: Ban, title: t('demandes.actions.CANCELLED') };
      default:
        return { Icon: null, title: st };
    }
  };

  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        setLoading(true);
        const data = await sessionService.supplierOrders({ order_type_group: typeFilter });
        const results = data?.results || [];
        setItems(Array.isArray(results) ? results : []);
      } catch (e) {
        console.error('Error fetching demandes:', e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDemandes();
  }, [typeFilter]);

  const statusOptions = useMemo(() => ([
    'PENDING_LOGISTICS',
    'PENDING_MANAGER',
    'NEW',
    'ACCEPTED',
    'SHIPPED',
    'RECEIVED',
    'INVOICED',
    'CANCELLED',
  ]), []);

  const filtered = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return items.filter((it) => {
      if (statusFilter && it.status !== statusFilter) return false;
      if (!s) return true;
      const title = (it.formation_title || '').toLowerCase();
      const trainer = (it.trainer_name || '').toLowerCase();
      const supplier = (it.supplier_name || '').toLowerCase();
      const orderType = (it.order_type_label || it.order_type || '').toLowerCase();
      const lines = (it.lines || []).map((ln) => ln.consommable_name || '').join(' ').toLowerCase();
      return title.includes(s) || trainer.includes(s) || supplier.includes(s) || orderType.includes(s) || lines.includes(s);
    });
  }, [items, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter, pageSize]);

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, pageSize, safeCurrentPage]);

  const orderStatusLabel = (status) => {
    switch (status) {
      case 'PENDING_LOGISTICS':
        return t('demandes.status.PENDING_LOGISTICS');
      case 'PENDING_MANAGER':
        return t('demandes.status.PENDING_MANAGER');
      case 'NEW':
        return t('demandes.status.NEW');
      case 'ACCEPTED':
        return t('demandes.status.ACCEPTED');
      case 'SHIPPED':
        return t('demandes.status.SHIPPED');
      case 'RECEIVED':
        return t('demandes.status.RECEIVED');
      case 'INVOICED':
        return t('demandes.status.INVOICED');
      case 'CANCELLED':
        return t('demandes.status.CANCELLED');
      default:
        return status || '-';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING_LOGISTICS':
        return 'status-pending';
      case 'PENDING_MANAGER':
        return 'status-approved';
      case 'NEW':
        return 'status-pending';
      case 'ACCEPTED':
        return 'status-approved';
      case 'SHIPPED':
        return 'status-approved';
      case 'RECEIVED':
        return 'status-approved';
      case 'INVOICED':
        return 'status-completed';
      case 'CANCELLED':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const getAllowedNextStatuses = (row) => {
    const current = row?.status;
    const isRestock = row?.order_type === 'STOCK_RESTOCK';
    if (isRestock && current === 'PENDING_LOGISTICS') {
      return (isLogistiqueCoordinator || isAdmin) ? ['PENDING_MANAGER', 'CANCELLED'] : [];
    }
    if (isRestock && current === 'PENDING_MANAGER') {
      return (isManager || isAdmin) ? ['NEW', 'CANCELLED'] : [];
    }

    switch (current) {
      case 'NEW':
        return ['ACCEPTED', 'CANCELLED'];
      case 'ACCEPTED':
        return ['SHIPPED', 'CANCELLED'];
      case 'SHIPPED':
        return ['RECEIVED'];
      case 'RECEIVED':
        return ['INVOICED'];
      default:
        return [];
    }
  };

  const handleOrderStatusChange = async (row, newStatus) => {
    if (!row?.order_id || !newStatus || newStatus === row.status) return;
    try {
      await sessionService.updateSupplierOrderStatus(row.order_id, newStatus);
      setItems((prev) => prev.map((it) => (
        it.order_id === row.order_id
          ? { ...it, status: newStatus, status_display: orderStatusLabel(newStatus) }
          : it
      )));
      toast.success(t('demandes.toasts.statusUpdated'));
    } catch (e) {
      toast.error(t('demandes.toasts.statusUpdateError'));
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="sessions-page demandes-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <Calendar size={32} />
            {t('nav.demandes')}
          </h1>
          <p className="page-subtitle">{t('dashboard.recentOrders.title')}</p>
        </div>
        <select
          className="filter-select"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <option value="">{t('demandes.filters.allTypes')}</option>
          <option value="SESSION">{t('demandes.filters.sessions')}</option>
          <option value="STOCK_RESTOCK">{t('demandes.filters.restock')}</option>
        </select>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={t('sessions.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={() => setSearchTerm('')}
              title={t('common.clearSearch')}
              type="button"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="">{t('demandes.filters.allStatuses', { defaultValue: 'Tous les statuts' })}</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>{orderStatusLabel(status)}</option>
          ))}
        </select>
      </div>

      <div className="demandes-table-wrapper table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('sessions.table.formation')}</th>
              <th>{t('demandes.table.type')}</th>
              <th>{t('demandes.table.supplier')}</th>
              <th>{t('demandes.table.requestDate')}</th>
              <th>{t('demandes.table.quote')}</th>
              <th>{t('demandes.table.supplies')}</th>
              <th>{t('sessions.table.status')}</th>
              <th className="actions-column">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                  {t('sessions.empty')}
                </td>
              </tr>
            ) : (
              paginatedItems.map((row) => (
                <tr key={row.order_id || row.session_id}>
                  <td className="name-cell">{row.formation_title}</td>
                  <td>{row.order_type_label || (row.order_type === 'STOCK_RESTOCK' ? t('demandes.orderTypes.stockRestock') : t('demandes.orderTypes.session'))}</td>
                  <td>{row.supplier_name || '-'}</td>
                  <td>
                    <div>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString(i18n.language) : '-'}
                    </div>
                  </td>
                  <td>
                    {row.total_devis ? toMoney(row.total_devis, row) : toMoney(0, row)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      {(row.lines || []).slice(0, 4).map((ln) => (
                        <div key={ln.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <Package size={14} />
                          <span>{ln.consommable_name}</span>
                          <strong>× {ln.quantity}</strong>
                        </div>
                      ))}
                      {(row.lines || []).length > 4 && (
                        <div style={{ opacity: 0.7 }}>
                          {t('demandes.otherItems', { count: (row.lines || []).length - 4 })}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <span className={`status-badge ${getStatusBadgeClass(row.status)}`}>
                        {row.status_display || orderStatusLabel(row.status)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="actions-cell">
                      <Link
                        to={`/mes-demandes/${row.order_id}`}
                        className="action-btn"
                        title={t('common.view')}
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        <Eye size={16} />
                      </Link>
                      {canEditDemandes && getAllowedNextStatuses(row).map((st) => {
                        const meta = statusActionMeta(st);
                        const Icon = meta.Icon;
                        return (
                          <button
                            key={st}
                            type="button"
                            className="action-btn"
                            title={meta.title}
                            onClick={() => handleOrderStatusChange(row, st)}
                            style={{ display: 'inline-flex', alignItems: 'center' }}
                          >
                            {Icon ? <Icon size={16} /> : meta.title}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="demandes-mobile-list">
        {paginatedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            {t('sessions.empty')}
          </div>
        ) : (
          paginatedItems.map((row) => (
            <div key={row.order_id || row.session_id} className="demande-card">
              <div className="demande-card-header">
                <div className="demande-card-title">{row.formation_title}</div>
                <span className={`status-badge ${getStatusBadgeClass(row.status)}`}>
                  {row.status_display || orderStatusLabel(row.status)}
                </span>
              </div>
              <div className="demande-card-trainer">{row.trainer_name}</div>
              <div className="demande-card-trainer">
                {row.order_type_label || (row.order_type === 'STOCK_RESTOCK' ? t('demandes.orderTypes.stockRestock') : t('demandes.orderTypes.session'))}
                {row.supplier_name ? ` - ${row.supplier_name}` : ''}
              </div>
              <div className="demande-card-meta">
                <div className="demande-card-meta-item">
                  <span className="demande-card-meta-label">{t('demandes.table.requestDate')}</span>
                  <span className="demande-card-meta-value">
                    {row.created_at ? new Date(row.created_at).toLocaleDateString(i18n.language) : '-'}
                  </span>
                </div>
                <div className="demande-card-meta-item">
                  <span className="demande-card-meta-label">{t('demandes.table.quote')}</span>
                  <span className="demande-card-meta-value">
                    {row.total_devis ? toMoney(row.total_devis, row) : toMoney(0, row)}
                  </span>
                </div>
              </div>
              <div className="demande-card-consommables">
                {(row.lines || []).slice(0, 3).map((ln) => (
                  <div key={ln.id} className="demande-card-consommable">
                    <Package size={14} />
                    <span>{ln.consommable_name}</span>
                    <strong>× {ln.quantity}</strong>
                  </div>
                ))}
                {(row.lines || []).length > 3 && (
                  <div style={{ opacity: 0.7, fontSize: '0.85rem' }}>
                    {t('demandes.otherItems', { count: (row.lines || []).length - 3 })}
                  </div>
                )}
              </div>
              <div className="demande-card-actions">
                <Link
                  to={`/mes-demandes/${row.order_id}`}
                  className="action-btn action-view"
                  title={t('common.view')}
                >
                  <Eye size={16} />
                </Link>
                {canEditDemandes && getAllowedNextStatuses(row).map((st) => {
                  const meta = statusActionMeta(st);
                  const Icon = meta.Icon;
                  return (
                    <button
                      key={st}
                      type="button"
                      className={`action-btn action-btn-${st.toLowerCase()}`}
                      title={meta.title}
                      onClick={() => handleOrderStatusChange(row, st)}
                    >
                      {Icon ? <Icon size={16} /> : meta.title}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="demandes-pagination-footer">
        <div className="pagination-info">
          {t('demandes.pagination.summary', {
            total: totalCount,
            currentPage: safeCurrentPage,
            totalPages,
            defaultValue: `${totalCount} demande(s) • Page ${safeCurrentPage} / ${totalPages}`,
          })}
        </div>
        <div className="pagination">
          <select
            className="filter-select demandes-page-size-select"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value) || 10)}
          >
            {[10, 20, 50].map((size) => (
              <option key={size} value={size}>{t('demandes.pagination.perPage', { count: size, defaultValue: `${size} / page` })}</option>
            ))}
          </select>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={safeCurrentPage <= 1}
          >
            {t('common.previous')}
          </button>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={safeCurrentPage >= totalPages}
          >
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemandesPage;
